import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./NookDragon.scss";

const DRAGON_SRC = "/images/nook-dragon.png";
const WHOOSH_SRC = "/audio/fireball-whoosh.mp3";

// Measured from floraphonic-fireball-whoosh-5 (afconvert → 16-bit WAV @ 48 kHz,
// then Python wave + array): duration 2088 ms. Main transient = peak 5 ms RMS
// at 366.5 ms (first energy spike; 80% peak-amp onset 365.5 ms). Sample-peak
// amplitude is later (506 ms) in the sustained roar — not the whoosh hit.
const WHOOSH_PEAK_MS = 366;
const WHOOSH_VOLUME = 0.55;
// Fire CSS (`nook-dragon-fire`) starts the instant phase becomes "breath".
const FIRE_VISUAL_MS = 2100;

let whooshAudio = null;
let whooshUnlock = null;

function getWhooshAudio() {
  if (typeof Audio === "undefined") return null;
  if (!whooshAudio) {
    whooshAudio = new Audio(WHOOSH_SRC);
    whooshAudio.preload = "auto";
    whooshAudio.loop = false;
    whooshAudio.volume = WHOOSH_VOLUME;
    whooshAudio.load();
  }
  return whooshAudio;
}

// Call from the candle click (user gesture) so later timed play() is allowed.
export function unlockNookWhoosh() {
  const audio = getWhooshAudio();
  if (!audio) return;
  const token = {};
  whooshUnlock = token;
  audio.muted = true;
  const play = audio.play();
  if (play && typeof play.then === "function") {
    play
      .then(() => {
        if (whooshUnlock !== token) return;
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
      })
      .catch(() => {
        if (whooshUnlock === token) audio.muted = false;
      });
  } else {
    audio.pause();
    audio.currentTime = 0;
    audio.muted = false;
  }
}

function playWhooshSynced() {
  const audio = getWhooshAudio();
  if (!audio) return;
  whooshUnlock = null;
  audio.pause();
  audio.muted = false;
  audio.loop = false;
  audio.volume = WHOOSH_VOLUME;
  audio.currentTime = 0;
  const play = audio.play();
  if (play && typeof play.catch === "function") play.catch(() => {});
}

function stopWhoosh() {
  whooshUnlock = null;
  if (!whooshAudio) return;
  whooshAudio.pause();
  whooshAudio.currentTime = 0;
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Open jaws: upper-center of the spread-wing pose, facing the viewer's right.
const MOUTH_X = 0.545;
const MOUTH_Y = 0.16;
const BREATH_GAP = 0.28;

function measureHover(stageEl, wickEl) {
  if (!stageEl || !wickEl) {
    return { hoverX: 520, hoverY: 980 };
  }
  const stage = stageEl.getBoundingClientRect();
  const wick = wickEl.getBoundingClientRect();
  const aimX = wick.left + wick.width / 2 - stage.left;
  const aimY = wick.top + wick.height / 2 - stage.top;
  const mobile =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 640px)").matches;
  const dw = mobile ? 164 : 220;
  const dh = mobile ? 113 : 152;
  // Hover so the mouth sits just left of the wick; fire reaches it.
  return {
    hoverX: Math.round(aimX - dw * MOUTH_X - dw * BREATH_GAP),
    hoverY: Math.round(aimY - dh * MOUTH_Y),
  };
}

// Isolated Book Nook easter egg: after the center candle is blown out,
// a silhouette flies in, breathes on the wick, then leaves.
function NookDragon({ playing, wickRef, onRelight, onDone }) {
  const stageRef = useRef(null);
  const onRelightRef = useRef(onRelight);
  const onDoneRef = useRef(onDone);
  const [phase, setPhase] = useState("wait");
  const [hover, setHover] = useState({ hoverX: 520, hoverY: 980 });

  onRelightRef.current = onRelight;
  onDoneRef.current = onDone;

  useEffect(() => {
    getWhooshAudio();
  }, []);

  useLayoutEffect(() => {
    if (!playing) return;
    setHover(measureHover(stageRef.current, wickRef && wickRef.current));
  }, [playing, wickRef]);

  useEffect(() => {
    if (!playing) {
      setPhase("wait");
      return undefined;
    }

    const timers = [];
    const later = (fn, ms) => {
      timers.push(setTimeout(fn, ms));
    };

    if (prefersReducedMotion()) {
      // No flight / fire visual — skip the whoosh.
      later(() => onRelightRef.current?.(), 480);
      later(() => onDoneRef.current?.(), 900);
      return () => {
        timers.forEach(clearTimeout);
        stopWhoosh();
      };
    }

    // Peak of the file must land on the fire frame:
    //   whooshStart + WHOOSH_PEAK_MS === FIRE_VISUAL_MS
    const whooshStartMs = FIRE_VISUAL_MS - WHOOSH_PEAK_MS;
    if (whooshStartMs >= 0) {
      later(playWhooshSynced, whooshStartMs);
    } else {
      later(() => {
        const audio = getWhooshAudio();
        if (!audio) return;
        whooshUnlock = null;
        audio.pause();
        audio.muted = false;
        audio.loop = false;
        audio.volume = WHOOSH_VOLUME;
        audio.currentTime = -whooshStartMs / 1000;
        const play = audio.play();
        if (play && typeof play.catch === "function") play.catch(() => {});
      }, 0);
    }

    // Short beat after the flame dies, then the flight.
    later(() => setPhase("enter"), 420);
    later(() => setPhase("breath"), FIRE_VISUAL_MS);
    later(() => onRelightRef.current?.(), 2480);
    later(() => setPhase("leave"), 3000);
    later(() => onDoneRef.current?.(), 4900);

    return () => {
      timers.forEach(clearTimeout);
      stopWhoosh();
    };
  }, [playing]);

  if (!playing) return null;

  return (
    <div className="nook-dragon-stage" ref={stageRef} aria-hidden="true">
      {phase !== "wait" && (
        <div
          className={`nook-dragon nook-dragon--${phase}`}
          style={{
            "--hover-x": `${hover.hoverX}px`,
            "--hover-y": `${hover.hoverY}px`,
            "--dragon-src": `url(${DRAGON_SRC})`,
          }}
        >
          <span className="nook-dragon-bird">
            <span className="nook-dragon-wing nook-dragon-wing--l" />
            <span className="nook-dragon-tail" />
            <span className="nook-dragon-body" />
            <span className="nook-dragon-wing nook-dragon-wing--r" />
            <span className="nook-dragon-fire" />
            <span className="nook-dragon-ember nook-dragon-ember--a" />
            <span className="nook-dragon-ember nook-dragon-ember--b" />
          </span>
        </div>
      )}
    </div>
  );
}

export default NookDragon;
